/**
 * Clerk stub for demo mode.
 *
 * `vite.config.ts` aliases `@clerk/react` (+ `/legacy`) to this module in
 * the `build:demo` build, so the whole app renders with a fixed signed-in
 * demo user + org and NO real Clerk — no external SDK, no publishable key,
 * no sign-in wall. Only the symbols the app imports from `@clerk/react`
 * are stubbed.
 *
 * Auth is faked here; data comes from `./demo-fixtures.ts` via `apiFetch`.
 *
 * TWO rules matter here:
 *   1. STABLE references. Every hook returns a module-constant object.
 *      Clerk hooks are consumed inside `useEffect` deps across the app;
 *      returning a fresh object per call would flip those deps every
 *      render and spin effects into an infinite loop (the Security page
 *      did exactly this).
 *   2. Writes are BLOCKED. Password change / 2FA / profile edits / org
 *      deletion go through the Clerk SDK, not apiFetch, so they wouldn't
 *      hit the apiFetch demo guard — block them here with the same toast.
 */
import type { PropsWithChildren, ReactElement } from 'react';
import { DEMO_ORG_ID } from './demo';
import { notifyDemoBlocked } from './errors';

const DEMO_USER_ID = 'user_demo00000000';
const DEMO_SESSION_ID = 'sess_demo00000000';
const noToken = async (): Promise<string | null> => null;
const noop = async (): Promise<void> => {};

/**
 * Lumina's org logo — a warm luminous orb on a dark tile, on-brand for a
 * lighting store. Inline SVG so the demo has no external asset + the org
 * mark shows Lumina's own logo instead of falling back to the Clickweave
 * wordmark (which read as "this is Clickweave's dashboard", not a tenant's).
 */
const LUMINA_LOGO = `data:image/svg+xml,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'>" +
    "<defs><radialGradient id='g' cx='50%' cy='38%' r='70%'>" +
    "<stop offset='0' stop-color='#FFF6DE'/><stop offset='.55' stop-color='#FFC24B'/>" +
    "<stop offset='1' stop-color='#E88A0B'/></radialGradient></defs>" +
    "<circle cx='20' cy='19' r='11' fill='url(#g)'/></svg>",
)}`;

/** Clerk SDK write → friendly demo toast + reject (blocks password, 2FA,
 *  profile edits, workspace deletion, etc.). */
const blockedClerkAction = async (): Promise<never> => {
  notifyDemoBlocked();
  throw new Error('This is a live demo — sign up for a free account to make changes.');
};

const USER = {
  id: DEMO_USER_ID,
  firstName: 'Demo',
  lastName: 'Viewer',
  fullName: 'Demo Viewer',
  primaryEmailAddress: { emailAddress: 'demo@lumina.store' },
  emailAddresses: [{ id: 'idn_demo', emailAddress: 'demo@lumina.store' }],
  imageUrl: '',
  passwordEnabled: true,
  totpEnabled: false,
  twoFactorEnabled: false,
  // Reads → safe empties.
  getSessions: async () => [],
  // Writes → blocked with the demo toast.
  updatePassword: blockedClerkAction,
  createTOTP: blockedClerkAction,
  disableTOTP: blockedClerkAction,
  createBackupCode: blockedClerkAction,
  verifyTOTP: blockedClerkAction,
  setProfileImage: blockedClerkAction,
  update: blockedClerkAction,
  delete: blockedClerkAction,
};

const ORGANIZATION = {
  id: DEMO_ORG_ID,
  name: 'Lumina',
  slug: 'lumina',
  imageUrl: LUMINA_LOGO,
  hasImage: true,
  membersCount: 1,
  update: blockedClerkAction,
  destroy: blockedClerkAction,
  delete: blockedClerkAction,
};

const SESSION = { id: DEMO_SESSION_ID, getToken: noToken };

const AUTH = {
  isLoaded: true,
  isSignedIn: true,
  userId: DEMO_USER_ID,
  sessionId: DEMO_SESSION_ID,
  orgId: DEMO_ORG_ID,
  orgRole: 'org:admin',
  orgSlug: 'lumina',
  actor: null,
  has: () => true,
  getToken: noToken,
  signOut: noop,
};

const USE_USER = { isLoaded: true, isSignedIn: true, user: USER };
const USE_ORGANIZATION = {
  isLoaded: true,
  organization: ORGANIZATION,
  membership: { role: 'org:admin' },
};
const USE_SESSION = { isLoaded: true, session: SESSION };
const CLERK = {
  signOut: noop,
  openSignIn: (): void => {},
  openUserProfile: (): void => {},
  setActive: noop,
  // Write → blocked (the waitlist route never renders in demo, but keep
  // the stub honest per rule #2 above).
  joinWaitlist: blockedClerkAction,
};
const USE_ORG_LIST = {
  isLoaded: true,
  userMemberships: {
    // One membership so the org switcher shows Lumina (its logo + active
    // check) instead of an empty "No organisations." popover.
    data: [{ id: 'orgmem_demo00000000', organization: ORGANIZATION }],
    count: 1,
    isLoading: false,
    hasNextPage: false,
  },
  setActive: noop,
};
const USE_SIGN_IN = { isLoaded: true, signIn: undefined, setActive: noop };
const USE_SIGN_UP = { isLoaded: true, signUp: undefined, setActive: noop };

export function ClerkProvider({ children }: PropsWithChildren): ReactElement {
  return <>{children}</>;
}

export function useAuth() {
  return AUTH;
}
export function useOrganization() {
  return USE_ORGANIZATION;
}
export function useUser() {
  return USE_USER;
}
export function useSession() {
  return USE_SESSION;
}
export function useClerk() {
  return CLERK;
}
export function useOrganizationList() {
  return USE_ORG_LIST;
}

// `@clerk/react/legacy` — used only by the auth routes, which never
// render in the demo.
export function useSignIn() {
  return USE_SIGN_IN;
}
export function useSignUp() {
  return USE_SIGN_UP;
}
